import { Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'
import { OnboardingStep, TrendTopic, TwinInsightCategory } from 'api/model'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { authenticatedUserAtom } from 'state'
import Insights from '../Insights/Insights'
import { COLORS } from 'app-constants'
import { SlackSettingsModal } from 'components/Organization/SlackSettings'
import TopicsSidebar from 'components/Insights/TopicsSidebar'
import { useFetchTwinsListInsights } from 'hooks/use-insights'
import TrendsSidebar from 'components/Insights/TrendsSidebar'
import { useFetchTrendTopics } from 'hooks/use-trends'
import { useFetchMyTwin } from 'hooks/use-twin'
import AcquisitionBanner from 'components/AcquisitionBanner/AcquisitionBanner'

const Home = () => {
  const isMobile = useCheckMobileScreen()
  const [user] = useRecoilState(authenticatedUserAtom)
  const { myTwin, refetchMyTwin } = useFetchMyTwin(user?.id ?? '')

  // make a fetch for the insights
  const {
    twinInsightsWithLatest,
    isLoading: isLoadingTwinsListInsights,
    refetchTwinInsightsWithLatest,
  } = useFetchTwinsListInsights(myTwin?.id ?? '', {
    include_latest: true,
  })

  // make a fetch for trends
  const { trendTopics, refetchTrendTopics } = useFetchTrendTopics(myTwin?.id ?? '')

  useEffect(() => {
    // every 24 hours, we should refetch the twin insights with latest
    const interval = setInterval(() => {
      refetchTwinInsightsWithLatest()
    }, 86400000)
    return () => clearInterval(interval)
  }, [refetchTwinInsightsWithLatest])

  useEffect(() => {
    // every five seconds, we check to see if insights onboarding is 100% and if it is, we stop refetching
    const interval = setInterval(() => {
      if (myTwin?.insights_onboarding_percent === 100) {
        clearInterval(interval)
        return
      }
      refetchTwinInsightsWithLatest()
      refetchTrendTopics()
      refetchMyTwin()
    }, 8000)
    return () => clearInterval(interval)
  }, [myTwin?.insights_onboarding_percent, refetchTwinInsightsWithLatest, refetchTrendTopics, refetchMyTwin])

  // used for scrolling the user to the selected category
  const [selectedCategory, setSelectedCategory] = useState<TwinInsightCategory | null>(null)
  // used for scrolling the user to the selected trend
  const [selectedTrend, setSelectedTrend] = useState<TrendTopic | null>(null)

  // clear out the selected trend if a category is selected
  useEffect(() => {
    if (selectedCategory) {
      setSelectedTrend(null)
    }
  }, [selectedCategory])

  // clear out the selected category if a trend is selected
  useEffect(() => {
    if (selectedTrend) {
      setSelectedCategory(null)
    }
  }, [selectedTrend])

  const hasShownSlackSettingsModal = user?.onboarding_steps_completed.includes(
    OnboardingStep.slack_settings_modal_shown
  )
  const [isSlackSettingsModalOpen, setIsSlackSettingsModalOpen] = useState(false)
  useEffect(() => {
    // we only show the settings modal if the user has slack enabled and has not seen the modal yet
    if (!hasShownSlackSettingsModal && !isSlackSettingsModalOpen && user?.has_slack) {
      setIsSlackSettingsModalOpen(true)
    }
  }, [hasShownSlackSettingsModal, isSlackSettingsModalOpen, user?.has_slack])

  const filteredTrendTopics = trendTopics.filter((trendTopic) => {
    // we only want to show the trend topics that has a corresponding insight in the twinInsightsWithLatest
    return twinInsightsWithLatest?.insights.some((insight) => insight.trend_topic?.id === trendTopic.id)
  })

  // get a list of the insight category ids
  const insightCategoryIds = twinInsightsWithLatest?.insights.map((insight) => insight.category.id) ?? []

  return (
    <Stack
      sx={{
        background: isMobile ? COLORS.GRAY_8 : 'transparent',
        height: isMobile ? '100vh' : 'auto',
      }}>
      {!isMobile && (
        <Stack
          py="40px"
          pl="41px"
          sx={{
            backgroundColor: COLORS.PURE_WHITE,
            borderBottom: `1px solid ${COLORS.GRAY_5}`,
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}>
          <Typography variant="h4">Insights</Typography>
        </Stack>
      )}

      <Stack mb={4}>
        <AcquisitionBanner />
      </Stack>

      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={isMobile ? 4 : 46}
        sx={{
          px: isMobile ? '16px' : '41px',
          pt: isMobile ? '16px' : '0px',
          maxWidth: isMobile ? 'inherit' : '100%',
        }}>
        {myTwin?.insights_onboarding_percent === 100 && (
          <Stack spacing={3} position={isMobile ? 'relative' : 'fixed'}>
            <TopicsSidebar
              selectedCategory={selectedCategory}
              refetchTwinInsightsWithLatest={refetchTwinInsightsWithLatest}
              setSelectedCategory={setSelectedCategory}
              insightCategoryIds={insightCategoryIds}
            />
            <TrendsSidebar trendTopics={filteredTrendTopics} setSelectedTrend={setSelectedTrend} />
          </Stack>
        )}
        <Insights
          twinInsightsWithLatest={twinInsightsWithLatest}
          isLoadingTwinListInsights={isLoadingTwinsListInsights}
          selectedCategory={selectedCategory}
          selectedTrend={selectedTrend}
          refetchTwinInsightsWithLatest={refetchTwinInsightsWithLatest}
        />
      </Stack>
      {isSlackSettingsModalOpen && (
        <SlackSettingsModal
          onClose={() => {
            setIsSlackSettingsModalOpen(false)
          }}
        />
      )}
    </Stack>
  )
}

export default Home
